import { useState, useEffect, useRef } from "react";
import { message, Modal, Form } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import db from "../../../../database";
import { CheckoutTotalManualDiscount } from "../PricingRules/CheckoutTotalManualDiscount";
import { CheckoutFlatDiscount } from "../PricingRules/CheckoutFlatDiscount";
import { CheckoutPercentageDiscount } from "../PricingRules/CheckoutPercentageDiscount";
import { advanceOrderFloorViolation } from "../../../../utils/advanceOrderValidation";
import PoleDisplay from "../../../../lib/printer/poleDisplay";
import CWPoleDisplay from "../../../../lib/printer/CWPoleDisplay";
import upsertPOSLog from "../../Retail/posLog";
import messages from "../../../../errors/messages.en-IN.json";
import { upsertGiftCardTransaction, upsertStoreDiscount } from "../../../../services";
import { getOAuthHeaders } from "../../../../constants/oAuthValidation";
import { useEventBus } from "../../../../lib/components/EventBusProvider";
import useCartStore from "../../../../stores/useCartStore";
import { trace } from "../../../../utils/debugTrace";
import useSafeTimeout from "../../../../hooks/useSafeTimeout";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import useAuthStore from "../../../../stores/useAuthStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import useRestaurantStore from "../../../../stores/useRestaurantStore";
import { SyncData } from "../../Restaurant/Tables-Management/SyncData";
import { roundTo, applyCurrencyRounding, getRoundingMethod, getPrecision } from "../../../../utils/tillHelpers";
import { formatDate } from "../posUtils";
import { checkPendingSwUpdate } from "../../../../sw-register";
import { getPricingRules } from "../../../../utils/pricingRulesCache";
import { getIsInterState } from "../../../../utils/taxUtils";
import { groupItemsByTaxRate, enrichLineItemFields } from "../utils/taxCalculations";
import { logSequenceEvent } from "../../../../utils/sequenceAudit";
import { numberToWords } from "../../../../utils/numberToWords";
import { isPrepareBillModificationBlocked, PREPARE_BILL_MODIFICATION_BLOCKED_MESSAGE } from "../../../../utils/prepareBillModificationGuard";

const useCartOperations = ({
  cart,
  setCart,
  addProduct,
  tillData,
  orderType,
  posConfig,
  precision,
  taxIncludeFlag,
  isExchange,
  searchInputRef,
  selectedSaleTypeData,
  defaultCustomer,
  giftCardData,
  setGiftCardData,
  posLogActivity,
  documentSequence,
  setAmount,
  setSelectedPaymentMethod,
  selectedReturnType,
  setDisplayManualQtyWeightInput,
  setAddOnsList,
  setSelectedProduct,
  setAddonSourceItem,
  setShowAddonsCustomizePopUp,
  setDisplayBatchSelection,
  selectedPaymentMethod,
  processPayment,
}) => {
  const tillaccess = JSON.parse(tillData?.tillAccess?.userAccessController);
  const tillLayout = parseInt(tillaccess?.layout === null || undefined ? 1 : tillaccess?.layout);
  const CWHardwareController = tillData.tillAccess.cwrTill.printTemplate.cwController === "Y" ? true : false;
  const { userData, tillValue } = useAuthStore.getState();
  const pricePrecision = tillData.tillAccess.csBunit.currencies[0].prcPrecision;
  const authHeaders = getOAuthHeaders();

  const { dispatchEvent, discountApproval, dispatchWeight } = useEventBus();

  // ---- Cart item selection state ----
  const { safeSetTimeout } = useSafeTimeout();
  const addQtyInFlight = useRef(false);
  const discountInFlightRef = useRef(false);
  const [isQtyUpdate, setIsQtyUpdate] = useState(false);
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [selectedProductInCart, setSelectedProductInCart] = useState({});
  const [selectedProductQty, setSelectProductQty] = useState(0);
  const [selectedProduct, setSelectedProductLocal] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [inputFocused, setInputFocused] = useState(null);
  const [realPrice, setRealPrice] = useState(0);
  const [firstInputValue, setFirstInputValue] = useState(false);
  const [qtyNumberFlag, setQtyNumberFlag] = useState(0);

  // ---- Manual discount state ----
  const [manualDiscountModalVisible, setManualDiscountModalVisible] = useState(false);
  const [manualDiscountInput, setManualDiscountInput] = useState("");
  const [manualDiscountTypes, setManualDiscountTypes] = useState([]);
  const [selectedManualDiscountType, setSelectedManualDiscountTypeValue] = useState("");
  const [enableManualDiscountInput, setEnableManualDiscountInput] = useState(false);
  const [manualDiscountForm] = Form.useForm();
  const [totalBillDiscountData, setTotalBillDiscountData] = useState({});
  const [discountAdditionalInfo, setDiscountAdditionalInfo] = useState([]);
  const [discountVerificationData, setDiscountVerifictionData] = useState(null);
  const [discountAdditionalInfoForm] = Form.useForm();
  const [selectedManualDiscountTypeValueState, setSelectedManualDiscountTypeValueState] = useState(null);

  // ---- Load manual discount types ----
  useEffect(() => {
    const pr = getPricingRules();
    const bunitId = tillData.tillAccess.csBunit.csBunitId;
    const manualPricingRules = pr.filter((rule) => {
      if (rule.manualDiscount === "Y") {
        if (rule.excludeBusinessUnits === "Y") {
          return !rule.mPricingBUnits?.some((bu) => bu.mBunitPricingId === bunitId);
        }
        if (rule.mPricingBUnits?.length > 0) {
          return rule.mPricingBUnits.some((bu) => bu.mBunitPricingId === bunitId);
        }
        return true;
      }
      return false;
    });
    setManualDiscountTypes(manualPricingRules);
  }, []);

  // ---- Cart item selection ----
  const selectProductInCart = (record, e) => {
    // BUG-1926: productId alone doesn't identify a row — a return line and its
    // matching exchange line share the same productId. Comparing isReturn too
    // stops this early-return branch from skipping the update when the click
    // actually moves between the two carts for the same product, which left
    // selectedProductInCart pointed at the old (return) line while
    // selectedRowKeys moved to the new (exchange) row.
    if (record.productId === selectedProductInCart.productId && record.isReturn === selectedProductInCart.isReturn && e !== "1") {
      setSelectedRowKeys([record.key]);
    } else {
      record.weight = parseFloat(parseFloat(record.weight)?.toFixed(record?.isQtyDesimal));
      setSelectedRowKeys([record.key]);
      setSelectedProductInCart(record);
    }
  };

  const clearSelectedProductInCart = () => {
    setSelectedProductInCart({});
    setSelectedRowKeys([]);
  };

  // ---- Quantity operations ----
  const enterTotalQty = async (type) => {
    const isExchangeLocal = useTransactionStore.getState().selectedReturnType === "EXCHANGE";

    // BUG-2115: read the live Zustand cart instead of the `cart` prop -- the prop can
    // lag one render behind (e.g. a pricing rule stamps discountName on the line
    // asynchronously after it was selected). The exchange branch already did this;
    // the default branch didn't, so the `product` lookup below could match against a
    // stale copy of the line, miss addProductToCart's index-matching, and create a
    // duplicate line instead of updating the existing one's quantity. Mirrors the
    // same fix already applied to increaseProductQty/decreaseProductQty.
    const activeCart = isExchangeLocal ? useCartStore.getState().exchangeCart || {} : useCartStore.getState().cart || cart;
    if (activeCart.payments.length > 0) {
      Modal.confirm({
        title: "Payment is in progress. You cannot modify cart items once payment has started. Please complete or cancel this payment to proceed",
        icon: null,
        okText: "Ok",
        cancelButtonProps: { style: { display: "none" } },
        autoFocusButton: null,
        okButtonProps: { style: { display: "inline-block" } },
        onOk() {
          const productSearchInput = document.getElementById("sm-product-search");
          if (productSearchInput) {
            safeSetTimeout(() => {
              productSearchInput.focus();
            }, 300);
          }
        },
      });
    } else {
      setDisplayManualQtyWeightInput(false);
      if (CWHardwareController) {
        CWPoleDisplay(selectedProductInCart, "Quantity update");
      } else {
        PoleDisplay(selectedProductInCart, "Quantity update");
      }
      const gatePassed = activeCart.items.length > 0 && selectedProductInCart.weight > 0 && !selectedProductInCart.isReturn;
      if (gatePassed) {
        let totalQtyFlag = true;
        setSelectedProductInCart({});
        let product = {};
        const addedToCart = activeCart.items;

        if (selectedProductInCart.groupedItems) {
          const parentItem = addedToCart.find((item) => item.children?.some((child) => child.childrenId === selectedProductInCart.childrenId));
          if (parentItem) {
            product =
              parentItem.children.find((childItem) => childItem.childrenId === selectedProductInCart.childrenId && childItem.lineId === selectedProductInCart.lineId) || product;
          }
        } else {
          product = addedToCart.find((item) => item.value === selectedProductInCart.value && item.lineId === selectedProductInCart.lineId) || product;
        }

        delete product.expiryId;
        setIsInputFocused(false);

        const performQtyAdd = () => {
          if (type === "weight") {
            addProduct(selectedProductInCart, selectedProductInCart.weight, totalQtyFlag, 0, false, true);
          } else {
            addProduct(product, selectedProductInCart.weight, totalQtyFlag, 0, false, true);
          }
          const amountInput = document.getElementById("sm-amount-input");
          if (amountInput) {
            amountInput?.focus();
            amountInput?.blur();
          }
          safeSetTimeout(() => {
            if (searchInputRef?.current) {
              searchInputRef?.current?.focus();
            }
          }, 100);
        };

        const qtyLimit = posConfig?.quantityConfirmationLimit || 0;
        if (selectedProductInCart.posPriceEdit === "Y" && qtyLimit && selectedProductInCart.weight > Number(qtyLimit)) {
          Modal.confirm({
            title: `You are adding a quantity of ${selectedProductInCart.weight}, which exceeds the limit of ${qtyLimit}. Do you want to proceed?`,
            icon: null,
            okText: "Ok",
            cancelText: "Cancel",
            autoFocusButton: null,
            onOk() {
              performQtyAdd();
            },
            onCancel() {
              safeSetTimeout(() => {
                if (searchInputRef?.current) {
                  searchInputRef?.current?.focus();
                }
              }, 100);
            },
          });
        } else {
          performQtyAdd();
        }
      }
    }
  };

  const enterRealPrice = async (type) => {
    setDisplayManualQtyWeightInput(false);
    if (CWHardwareController) {
      CWPoleDisplay(selectedProductInCart, "Quantity update");
    } else {
      PoleDisplay(selectedProductInCart, "Quantity update");
    }
    const isExchangeLocal = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
    // BUG-2115: same stale-prop fix as enterTotalQty above -- read the live Zustand
    // cart rather than the `cart` prop so this doesn't match against an outdated
    // copy of the line and spawn a duplicate line.
    const activeCart = isExchangeLocal ? useCartStore.getState().exchangeCart || {} : useCartStore.getState().cart || cart;
    if (activeCart.items.length > 0 && selectedProductInCart.weight > 0 && !selectedProductInCart.isReturn) {
      let totalQtyFlag = true;
      setSelectedProductInCart({});
      let product = {};
      const addedToCart = [...activeCart.items];

      const matchIndex = addedToCart.findIndex((item) => item.value === selectedProductInCart.value && item.lineId === selectedProductInCart.lineId);
      if (matchIndex !== -1) {
        product = { ...addedToCart[matchIndex], realPrice: Number(realPrice) };
        addedToCart[matchIndex] = {
          ...addedToCart[matchIndex],
          realPrice: Number(realPrice),
          salePrice: Number(realPrice),
          originalPrice: Number(realPrice),
          sunitprice: Number(realPrice),
        };
        setSelectedProductInCart(addedToCart[matchIndex]);
      }
      const updatedCart = {
        ...activeCart,
        items: addedToCart,
      };
      if (isExchangeLocal) {
        useCartStore.getState().setExchangeCart(updatedCart);
      } else {
        setCart(updatedCart);
      }
      delete product.expiryId;
      setIsInputFocused(false);
      if (type === "weight") {
        addProduct(selectedProductInCart, selectedProductInCart.weight, totalQtyFlag, 0, false, true);
      } else {
        addProduct(product, selectedProductInCart.weight, totalQtyFlag, 0, false, true);
      }

      const amountInput = document.getElementById("sm-amount-input");
      if (amountInput) {
        amountInput?.focus();
        amountInput?.blur();
      }
    }
  };

  const onChangeTotalQuantity = async (e) => {
    trace("SMQtyInput", "onChangeTotalQuantity entry", {
      typedValue: e,
      currentWeight: selectedProductInCart?.weight,
      lineId: selectedProductInCart?.lineId,
      isReturn: selectedProductInCart?.isReturn,
      gatePassed: !selectedProductInCart.isReturn && Object.keys(selectedProductInCart).length > 0,
    });
    if (!selectedProductInCart.isReturn && Object.keys(selectedProductInCart).length > 0) {
      delete selectedProductInCart.expiryId;
      let newWeight = selectedProductInCart.isDecimalQty === true ? e : e.replaceAll(".", "");
      const numWeight = Number(newWeight);
      const stockQty = selectedProductInCart.stock ?? selectedProductInCart.availableQty;
      if (posConfig?.enableStockCheck === "Y" && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y" && stockQty != null && numWeight > stockQty) {
        message.warning(`Only ${stockQty} units available in stock.`);
        newWeight = stockQty;
      }
      trace("SMQtyInput", "onChangeTotalQuantity: setting weight", { newWeight });
      setSelectedProductInCart({ ...selectedProductInCart, weight: newWeight });
    } else {
      trace("SMQtyInput", "onChangeTotalQuantity: gate blocked, no-op", {});
    }
  };

  const handleTotalQty = async (value) => {
    trace("SMQtyInput", "handleTotalQty entry", {
      value,
      qtyNumberFlag,
      currentWeight: selectedProductInCart?.weight,
      lineId: selectedProductInCart?.lineId,
      productId: selectedProductInCart?.productId,
      isReturn: selectedProductInCart?.isReturn,
      restrictPosQtyEdit: selectedProductInCart?.restrictPosQtyEdit,
      gatePassed: Object.keys(selectedProductInCart).length > 0 && !selectedProductInCart.isReturn && selectedProductInCart?.restrictPosQtyEdit !== "Y",
    });
    if (Object.keys(selectedProductInCart).length > 0 && !selectedProductInCart.isReturn && selectedProductInCart?.restrictPosQtyEdit !== "Y") {
      if (selectedProductQty === "" && value === "x") {
        trace("SMQtyInput", "handleTotalQty branch: backspace-on-empty -> 0", {});
        setSelectedProductInCart({ ...selectedProductInCart, weight: 0 });
      } else if (value === "x") {
        const newWeight = `${selectedProductInCart.weight.toString().substring(0, selectedProductInCart.weight.toString().length - 1)}`;
        trace("SMQtyInput", "handleTotalQty branch: backspace", { fromWeight: selectedProductInCart.weight, toWeight: newWeight });
        setSelectedProductInCart({ ...selectedProductInCart, weight: newWeight });
      } else if (value === "clear") {
        trace("SMQtyInput", "handleTotalQty branch: clear -> 0", {});
        setSelectedProductInCart({ ...selectedProductInCart, weight: 0 });
      } else {
        if (qtyNumberFlag === 0) {
          trace("SMQtyInput", "handleTotalQty branch: REPLACE (flag was 0)", { fromWeight: selectedProductInCart.weight, toWeight: value });
          setQtyNumberFlag(1);
          setSelectedProductInCart({ ...selectedProductInCart, weight: value });
        } else {
          const appendedWeight =
            selectedProductInCart.isDecimalQty === true ? `${selectedProductInCart.weight}${value}` : `${selectedProductInCart.weight}${value}`.replaceAll(".", "");
          trace("SMQtyInput", "handleTotalQty branch: APPEND (flag was 1)", { fromWeight: selectedProductInCart.weight, toWeight: appendedWeight });
          setSelectedProductInCart({
            ...selectedProductInCart,
            weight: appendedWeight,
          });
        }
      }
    } else {
      trace("SMQtyInput", "handleTotalQty: gate blocked, no-op", {});
    }
    const amountInput = document.getElementById("sm-amount-input");
    if (amountInput) {
      amountInput?.focus();
    }
  };

  const handleRealPrice = (value) => {
    let updatedPrice = realPrice;

    if (value === "x") {
      updatedPrice = realPrice.slice(0, -1);
    } else if (value === "clear") {
      updatedPrice = "";
    } else if (value === ".") {
      if (!realPrice.includes(".")) {
        updatedPrice += ".";
      }
    } else {
      if (firstInputValue) {
        updatedPrice = value;
        setFirstInputValue(false);
      } else {
        updatedPrice += value;
      }
    }

    setRealPrice(updatedPrice);
  };

  // ---- Delete / qty change operations ----
  const deleteProduct = async (addToCart) => {
    try {
      clearSelectedProductInCart();

      // In exchange mode, the active cart is the exchange cart, not the
      // return cart. Validation must check the correct cart. (Issue #12)
      const isExchangeMode = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
      const activeCartForValidation = isExchangeMode ? useCartStore.getState().exchangeCart || {} : cart;

      if (activeCartForValidation.payments?.length > 0) {
        message.warning(`${messages.errors.cart.delete_payment_first}`);
        return;
      } else if (addToCart.isReturn) return;

      // BUG-2020 is scoped to GPR's /preorder retrieval flow only, not every client's
      // Advance/Layaway usage (Chocolala retrieves and edits Advance carts directly on
      // /pos, a materially different flow). isRetrived is stamped unconditionally by
      // selectLayAwayOrder for ANY retrieval though (Advance/Layaway or a plain parked
      // bill), so this guard must only be narrowed for the Advance/Layaway case, on the
      // /preorder screen specifically -- a genuine parked bill keeps today's unconditional
      // protection everywhere, unchanged.
      const isAdvanceOrderRetrieval = activeCartForValidation.layAway === "Y" || activeCartForValidation.docType === "Advance";
      const advanceLastItemGuardApplies = !isAdvanceOrderRetrieval || window.location.pathname.includes("/preorder");

      if (activeCartForValidation.isRetrived === "Y" && activeCartForValidation.items.length === 1 && advanceLastItemGuardApplies) {
        message.warning(`${messages.errors.cart.cannot_delete_parked_item}`);
        return;
      } else if (
        activeCartForValidation.isRetrived === "Y" &&
        activeCartForValidation.items.length > 1 &&
        addToCart?.type !== "S" &&
        addToCart?.productSegment !== "" &&
        addToCart?.productSegment !== "AD" &&
        advanceLastItemGuardApplies
      ) {
        message.warning(`${messages.errors.cart.cannot_delete_parked_item}`);
        return;
      }

      const performDelete = async () => {
        const deleteProductInCart = true;
        await addProduct(addToCart, -addToCart.weight, false, 0, deleteProductInCart);
        if (giftCardData?.length > 0) {
          let authToken = authHeaders?.access_token || "";
          const { referenceId } = activeCartForValidation;
          const giftCardDetails = giftCardData.map(
            (ele) => `{
            cardNo: ${ele.number ? `"${ele.number}"` : null},
            type: "RD",
            referenceNo: "${referenceId}",
            amount: ${ele.amount * -1},
            cardPin: ${ele.pin ? `"${ele.pin}"` : null}
            tillId: ${tillData?.tillAccess?.cwrTill?.cwrTillID ? `"${tillData?.tillAccess?.cwrTill?.cwrTillID}"` : null}
            b2c_Customer_Id: ${activeCartForValidation.customer?.cwrCustomerId ? `"${activeCartForValidation.customer.cwrCustomerId}"` : null}
          }`
          );
          const query = `
            mutation {
              upsertGiftCardTransaction(giftCardTransactions: [${giftCardDetails.join(",")}]) {
                status
                message
              }
            }`;

          await upsertGiftCardTransaction(query);

          // Only mutate the RETURN cart's gift-card payments when we're NOT in
          // exchange mode. In exchange mode, deleting a product from the
          // exchange cart should not strip gift-card payments from the return
          // cart — they belong to different line sets. Ported from roadmap
          // 1ec48549.
          if (!isExchangeMode) {
            setCart((prevCart) => {
              const updatedPayments = prevCart.payments.filter((payment) => payment.name.toLowerCase() !== "gift card");

              // BUG-2116: cart.total is always a fresh sum over remaining items
              // (see cart.js addProductToCart) — it never includes payment amounts,
              // so it must not be adjusted here. This block only runs once the
              // deleteProduct guard above has already confirmed cart.payments is
              // empty (payment deleted separately), meaning prevCart.total was
              // already correctly recomputed by addProduct just above. Previously
              // this subtracted the gift-card amount a second time, driving total
              // negative once the last item was deleted.
              return {
                ...prevCart,
                payments: updatedPayments,
                giftCardData: [],
              };
            });
            setGiftCardData([]);
          }
        }

        const posConfigLocal = usePOSConfigStore.getState().posConfig || {};
        if (posConfigLocal?.DLN === "Y") {
          posLogActivity(addToCart, "DLN");
        }
      };

      // BUG-2151: deleting the last remaining item silently drops whatever discount was
      // riding on the bill along with it (a per-line manual discount on this item, or a
      // bill-level manual/coupon Total Bill Discount) with no warning -- the cashier only
      // discovers the discount is gone after the cart is already empty. Confirm first when
      // this delete would empty the cart and a discount/coupon is actually active; "No"
      // leaves the cart untouched (closes the popup, nothing is removed).
      const activeItemCount = (activeCartForValidation.items || []).filter((i) => i.weight !== 0 && i.isCanceled !== "Y").length;
      const isLastItem = activeItemCount <= 1;
      const hasActiveDiscount =
        Boolean(addToCart?.discountName) ||
        Boolean(activeCartForValidation?.totalBillDicount) ||
        Number(activeCartForValidation?.discount) > 0 ||
        activeCartForValidation?.manualDiscountData?.lineLevelDiscount?.length > 0 ||
        activeCartForValidation?.manualDiscountData?.totalLevelDiscount?.length > 0 ||
        activeCartForValidation?.couponInput?.length > 0;

      if (isLastItem && hasActiveDiscount) {
        Modal.confirm({
          title: "Remove last item?",
          icon: null,
          content: "A discount has already been applied to this bill. Removing this item will also remove the discount. Do you want to continue?",
          okText: "Yes",
          cancelText: "No",
          autoFocusButton: null,
          okButtonProps: { style: { display: "inline-block" } },
          async onOk() {
            // Reuse the exact same call the "Remove All Discounts" button in
            // ManualDiscountDrawer.js makes -- don't reimplement discount teardown
            // here, run the same already-tested path so this popup's "Yes" behaves
            // identically to explicitly clearing discounts first, then deleting.
            await removeAllDiscounts(true, false, true);
            await performDelete();
          },
        });
        return;
      }

      await performDelete();
    } catch (error) {
      message.error(error.message || `${messages.errors.cart.error_deleting_product}`);
    }
  };

  const decreaseProductQty = async (addToCart) => {
    if (cart.payments.length > 0) {
      Modal.confirm({
        title: "Payment is in progress. You cannot modify cart items once payment has started. Please complete or cancel this payment to proceed",
        icon: null,
        okText: "Ok",
        cancelButtonProps: { style: { display: "none" } },
        autoFocusButton: null,
        okButtonProps: { style: { display: "inline-block" } },
        onOk() {
          const productSearchInput = document.getElementById("sm-product-search");
          if (productSearchInput) {
            safeSetTimeout(() => {
              productSearchInput.focus();
            }, 300);
          }
        },
      });
    } else {
      if (!addToCart.isReturn) {
        // Manual weight handler
        if (addToCart.isManualQty) {
          dispatchWeight(addToCart, setInputFocused, selectedProductInCart, setSelectedProductInCart, (updatedProduct) => {
            if (updatedProduct) {
              updatedProduct.lineId = addToCart.lineId;
              addProduct(updatedProduct, updatedProduct?.weight || 1, true, 0, false, true);
            }
          });
          return;
        }

        // Original logic
        if (!addToCart.isManualQty) {
          if (addToCart.weight - 1 !== 0) {
            delete addToCart.expiryId;
          }
          const deleteQty = addToCart.weight - 1;
          if (cart.isRetrived === "Y" && cart.items.length === 1 && deleteQty <= 0) {
            message.warning(`${messages.errors.cart.cannot_delete_parked_item}`);
            return;
          }
          // Guard: skip concurrent rapid-key calls (see increaseProductQty above) —
          // otherwise a second click before the previous decrement lands recomputes
          // weight-1 off the same stale weight, so the display lags one click behind.
          if (addQtyInFlight.current) return;
          addQtyInFlight.current = true;
          try {
            const isExchangeQty = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
            const liveItems = isExchangeQty
              ? useCartStore.getState().exchangeCart?.items || []
              : useCartStore.getState().cart?.items || [];
            const liveItem = liveItems.find((i) => i.lineId === addToCart.lineId) || addToCart;
            await addProduct(liveItem, -1, false, 0, false, true);
            if (usePOSConfigStore.getState().posConfig?.RQT === "Y") {
              posLogActivity(liveItem, "RQT");
            }
            const amountInput = document.getElementById("sm-amount-input");
            if (amountInput) {
              amountInput?.focus();
              amountInput?.blur();
            }
          } finally {
            addQtyInFlight.current = false;
          }
        }
      }
    }
  };

  const increaseProductQty = async (addToCart) => {
    if (cart.payments.length > 0) {
      Modal.confirm({
        title: "Payment is in progress. You cannot modify cart items once payment has started. Please complete or cancel this payment to proceed",
        icon: null,
        okText: "Ok",
        cancelButtonProps: { style: { display: "none" } },
        autoFocusButton: null,
        okButtonProps: { style: { display: "inline-block" } },
        onOk() {
          const productSearchInput = document.getElementById("sm-product-search");
          if (productSearchInput) {
            safeSetTimeout(() => {
              productSearchInput.focus();
            }, 300);
          }
        },
      });
    } else {
      if (!addToCart.isReturn) {
        // Manual weight handler
        if (addToCart.isManualQty) {
          dispatchWeight(addToCart, setInputFocused, selectedProductInCart, setSelectedProductInCart, (updatedProduct) => {
            if (updatedProduct) {
              updatedProduct.lineId = addToCart.lineId;
              addProduct(updatedProduct, updatedProduct?.weight || 1, true, 0, false, true);
            }
          });
          return;
        } else if (addToCart?.productAddons?.length > 0 && tillLayout === 2) {
          setSelectedProduct(addToCart);
          const requiredListFilter = [...addToCart.productAddons].filter((item) => item.mAddonGroup.minqty === 1);

          const optionsListFilter = [...addToCart.productAddons].filter((item) => item.mAddonGroup.minqty === 0);

          setAddOnsList({
            requiredList: requiredListFilter || [],
            optionsList: optionsListFilter || [],
          });

          setAddonSourceItem(addToCart);
          setShowAddonsCustomizePopUp(true);
          return;
        }

        // Original logic
        if (!addToCart.isManualQty) {
          // Guard: skip concurrent rapid-key calls — the previous async addProduct
          // hasn't finished writing back to Zustand yet, so a second concurrent
          // call would read stale state and create a duplicate cart line.
          if (addQtyInFlight.current) return;
          addQtyInFlight.current = true;
          try {
            // Read the live item from the Zustand store instead of using the
            // potentially stale selectedProductInCart ref.  Pricing rules (e.g.
            // BuyXPayYOfSame / BUY 1 GET 1) stamp discountName on the cart item
            // AFTER addProductToCart returns; if we pass the stale ref the initial
            // findIndex check (!p.discountName) fails and a new line is created.
            const isExchangeQty = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
            const liveItems = isExchangeQty
              ? useCartStore.getState().exchangeCart?.items || []
              : useCartStore.getState().cart?.items || [];
            const liveItem = liveItems.find((i) => i.lineId === addToCart.lineId) || addToCart;
            const stockQty = liveItem.stock ?? liveItem.availableQty;
            if (posConfig?.enableStockCheck === "Y" && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y" && stockQty != null && Number(liveItem.weight) >= stockQty) {
              message.warning(`Only ${stockQty} units available in stock.`);
              return;
            }
            delete liveItem.expiryId;
            await addProduct(liveItem, 1, false, 0, false, true);
            const amountInput = document.getElementById("sm-amount-input");
            if (amountInput) {
              amountInput?.focus();
              amountInput?.blur();
            }
          } finally {
            addQtyInFlight.current = false;
          }
        }
      }
    }
  };

  // ---- Delete receipt / cart ----
  const deleteReceipt = (param, exchangeFlag, nextDocumentNo) => {
    let cartObj = {
      items: [],
      couponInput: [],
      advancePayment: 0,
      total: 0,
      tax: 0,
      discount: 0,
      paid: 0,
      change: 0,
      totalQty: 0,
      roundOff: 0,
      payments: [],
      redemptionPoints: 0,
      accumulationPoints: 0,
      creditAmount: 0,
      docType: "Invoice",
      sOrderID: uuidv4().replace(/-/g, "").toUpperCase(),
      referenceId: uuidv4().replace(/-/g, "").toUpperCase(),
      giftCardRefId: uuidv4().replace(/-/g, "").toUpperCase(),
      couponRefId: uuidv4().replace(/-/g, "").toUpperCase(),
      customer: selectedSaleTypeData?.customer?.b2cCustomerId ? selectedSaleTypeData?.customer : defaultCustomer,
      salesRepId: null,
      cardPaymentData: {},
      documentno:
        nextDocumentNo !== undefined
          ? nextDocumentNo
          : cart?.parked === "Y"
            ? `${tillData.tillAccess.cwrTill.prefix}${documentSequence + 1}`
            : param === "layAwayParked" || param
              ? `${tillData.tillAccess.cwrTill.prefix}${documentSequence + 1}`
              : `${tillData.tillAccess.cwrTill.prefix}${documentSequence + 2}`,
      userName: userData?.user ?? "",
      tillName: tillValue?.name,
      subTotal: 0,
      itemsSummery: {},
      amountInWords: "",
      groupedItemsByTaxRate: {},
      serviceCharge: 0,
      tip: 0,
      deiveryCharge: 0,
      packingCharge: 0,
      others: 0,
    };
    // A cart abandoned before any customer/payment step (the earliest points that
    // otherwise log anything) leaves zero trace of this preview number ever being
    // issued — this is the only record that it was shown on screen at all.
    logSequenceEvent("preview-shown", { documentno: cartObj.documentno, sOrderID: cartObj.sOrderID, flow: "deleteReceipt" }, db);
    if (!exchangeFlag) {
      setCart(cartObj);
    }
    // BUG-1186: Only clear exchange cart state when there is no pending exchange
    // order to process. During Return & Exchange flow, the return order completes
    // first and calls deleteReceipt via deleteCart. If we clear exchange state here,
    // the exchange cart's posOrderProcessor1 can never run. The caller passes
    // exchangeFlag=true when the exchange cart still needs processing.
    if (!exchangeFlag) {
      // DI-26: Always clear exchange cart to prevent stale exchange items
      // useCartStore.getState().setExchangeCart(null);
      // localStorage.removeItem("exchangeCartObj");
      // BUG-1188: Preserve selectedReturnType for STORE_CREDIT (wallet refund drawer needs it).
      // EXCHANGE is already protected by the outer exchangeFlag guard (BUG-1186).
      const currentReturnType = useTransactionStore.getState().selectedReturnType;
      if (currentReturnType !== "STORE_CREDIT") {
        useTransactionStore.getState().setSelectedReturnType(null);
      }
    }

    setSelectedRowKeys([]);
    setSelectedProductInCart({});
    setAmount("");
    if (selectedReturnType !== "STORE_CREDIT") {
      setSelectedPaymentMethod("");
    }
  };

  const deleteCart = (status = false, type, isExchangeFlag, nextDocumentNo) => {
    if (status === true) {
      if (usePOSConfigStore.getState().posConfig?.DOR === "Y") {
        upsertPOSLog(cart, "DOR");
      }
    }
    deleteReceipt(type, isExchangeFlag, nextDocumentNo);
    checkPendingSwUpdate();
  };

  // ---- Manual discount operations ----
  const applyManualDiscount = async (values) => {
    if (discountInFlightRef.current) return;
    // TSK-3303: once Prepare Bill has confirmed the order, discount changes are an
    // item-level modification and are blocked unless the till explicitly allows them.
    if (isPrepareBillModificationBlocked(useCartStore.getState().cart, posConfig)) {
      message.info(PREPARE_BILL_MODIFICATION_BLOCKED_MESSAGE);
      return;
    }
    discountInFlightRef.current = true;
    try {
    const isExchangeLocal = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
    const exchangeCartData = useCartStore.getState().exchangeCart;
    const activeCart = isExchangeLocal && exchangeCartData?.items?.length > 0 ? exchangeCartData : cart;
    const activeItems = activeCart.items.filter((i) => i.isCanceled !== "Y");

    if (activeItems.length === 0) {
      message.warning("No active items to apply discount");
      return;
    }
    const inputDiscountValue = values.discountValue || manualDiscountInput;
    // values.discountName can be blank even though a type is visibly selected: this
    // function resets the form immediately on submit, before an approval drawer (if
    // any) resolves. If that approval is closed/rejected, the hidden discountName
    // field is never repopulated on retry, so fall back to the id backing the
    // still-selected type instead of misreporting "invalid input" (BUG-1855).
    const discountName = values.discountName || selectedManualDiscountType?.mPricingrulesId;
    if (selectedManualDiscountType && discountName && inputDiscountValue && activeCart.items.length > 0) {
      const effectiveValues = { ...values, discountName, discountValue: inputDiscountValue };
      setManualDiscountInput("");
      setSelectedManualDiscountTypeValueState(null);
      manualDiscountForm.resetFields();
      const pr = getPricingRules();
      const pricingRule = pr[pr.findIndex((md) => md.mPricingrulesId === effectiveValues.discountName && md.manualDiscount === "Y")];
      if (pricingRule.discountType === "P" && effectiveValues.discountValue > 100 && pricingRule.type === "TD") {
        message.error(`${messages.errors.discounts.percentage_exceeds_limit}`);
        return;
      } else if (pricingRule.discountType === "V" && effectiveValues.discountValue > activeCart.total) {
        message.error(`${messages.errors.discounts.amount_exceeds_total}`);
        return;
      }

      if (effectiveValues.discountValue > 100 && pricingRule.type === "PD") {
        message.error(`${messages.errors.discounts.percentage_exceeds_limit}`);
        return;
      } else if (effectiveValues.discountValue > selectedProductInCart.nettotal && pricingRule.type === "FD") {
        message.error(`${messages.errors.discounts.amount_exceeds_total}`);
        return;
      }
      let approvalFlag = true;
      let discountValue = effectiveValues.discountValue;
      let matchingApprovals = [];
      const minValue = pricingRule?.mPricingApprovals?.reduce((min, item) => Math.min(min, item.min), Infinity);
      const maxValue = pricingRule?.mPricingApprovals?.reduce((max, item) => Math.max(max, item.max), -Infinity);
      if (pricingRule?.mPricingApprovals?.length > 0) {
        matchingApprovals = pricingRule.mPricingApprovals.filter((approval) => {
          if (approval.type === "P") {
            if (pricingRule.discountType === "V") {
              if (pricingRule.type === "TD") {
                discountValue = (effectiveValues.discountValue / activeCart.total) * 100;
              } else {
                discountValue = (effectiveValues.discountValue / selectedProductInCart.nettotal) * 100;
              }
              return discountValue >= approval.min && (approval?.max ? discountValue <= approval.max : true);
            } else {
              return effectiveValues.discountValue >= approval.min && (approval?.max ? effectiveValues.discountValue <= approval.max : true);
            }
          } else if (approval.type === "V") {
            if (pricingRule.discountType === "P") {
              if (pricingRule.type === "TD") {
                discountValue = (effectiveValues.discountValue / 100) * activeCart.total;
              } else {
                discountValue = (effectiveValues.discountValue / 100) * selectedProductInCart.nettotal;
              }
              return discountValue >= approval.min && (approval?.max ? discountValue <= approval.max : true);
            } else {
              return effectiveValues.discountValue >= approval.min && (approval?.max ? effectiveValues.discountValue <= approval.max : true);
            }
          }
        });

        useTransactionStore.getState().setDiscountAppliedData(totalBillDiscountData);

        if (pricingRule?.additionalInfo?.length > 0) {
          setDiscountVerifictionData({
            pricingRule: pricingRule,
            matchingApproval: matchingApprovals[0],
            matchingApprovals: matchingApprovals,
            values: effectiveValues,
            convertedDiscountValue: discountValue,
          });
          setDiscountAdditionalInfo(pricingRule.additionalInfo);
          return;
        }
        if (pricingRule?.enableApproval === "Y") {
          if (matchingApprovals.length === 0 && discountValue > minValue) {
            message.warning(`${messages.errors.discounts.approval_required}`);
            return;
          } else if (discountValue >= minValue && discountValue <= maxValue) {
            approvalFlag = await new Promise((resolve) => {
              discountApproval(matchingApprovals, pricingRule, null, totalBillDiscountData?.discountedAmount, totalBillDiscountData?.totalBillPrice, (eventData) => {
                resolve(eventData?.approvalGranted);
              });
            });
          } else if (discountValue > maxValue) {
            return;
          }
        }
      }

      if (!approvalFlag) return;
      processManualDiscount(pricingRule, matchingApprovals[0], effectiveValues);
    } else {
      setManualDiscountInput("");
      setSelectedManualDiscountTypeValueState(null);
      message.warning(`${messages.info.valid_input}`);
    }
    } finally {
      discountInFlightRef.current = false;
    }
  };

  const processManualDiscount = async (pricingRule, matchingApproval, values) => {
    setManualDiscountModalVisible(false);
    const minValue = pricingRule?.mPricingApprovals?.reduce((min, item) => Math.min(min, item.min), Infinity);
    const maxValue = pricingRule?.mPricingApprovals?.reduce((max, item) => Math.max(max, item.max), -Infinity);
    if (posConfig?.saveParkedBills === "Y" && pricingRule?.enableApproval === "Y" && pricingRule?.mPricingApprovals?.length > 0 && values.discountValue >= minValue && values.discountValue <= maxValue) {
      const csBunitId = tillData?.tillAccess?.csBunit?.csBunitId;
      const csUserId = matchingApproval?.userId;
      const cwrStoreDiscountId = uuidv4().replace(/-/g, "").toUpperCase();
      const cwrTillId = tillData?.tillAccess?.cwrTill?.cwrTillID;
      const status = "REQ";
      const cartItems = useCartStore.getState().cart;
      const source = "POS";
      const requestDate = formatDate();
      const discountType = pricingRule.discountType === "P" ? "PER" : "AMT";
      const discountValueNum = parseFloat(values.discountValue);
      const discountData = useTransactionStore.getState().discountAdditionalInfo;
      const discountAppliedValues = useTransactionStore.getState().discountAppliedData;
      const billGrossAmount = discountAppliedValues?.totalBillPrice;
      const requestedDiscountAmount = discountAppliedValues?.discountedAmount;
      const billNetAmountAfterDiscount = discountAppliedValues?.finalAmount;
      const reason = discountData?.Reason;

      const upsertStoreDiscountPayload = {
        csBunitId,
        csUserId,
        cwrStoreDiscountId,
        cwrTillId,
        status,
        cartItems,
        source,
        requestDate,
        discountType,
        discountValue: discountValueNum,
        billGrossAmount,
        requestedDiscountAmount,
        billNetAmountAfterDiscount,
        reason,
      };

      const response = await upsertStoreDiscount(upsertStoreDiscountPayload);
      if (response?.status === 200) {
        if (response?.data?.data?.upsertStoreDiscount?.status === "200") {
          message.success(response?.data?.data?.upsertStoreDiscount?.message);
        } else if (response?.data?.data?.upsertStoreDiscount?.status === "201") {
          message.error(response?.data?.data?.upsertStoreDiscount?.message);
          return;
        } else {
          message.error("Something is wrong in upsertStoreDiscount service");
          return;
        }
      } else {
        message.warning("Something is wrong in upsertStoreDiscount service");
        return;
      }
    }
    const isExchangeLocal = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
    const exchangeCartData = useCartStore.getState().exchangeCart;
    const activeCart = isExchangeLocal && exchangeCartData?.items?.length > 0 ? exchangeCartData : cart;
      // BUG-2020 (2026-09-01): CheckoutFlatDiscount/CheckoutPercentageDiscount/
      // CheckoutTotalManualDiscount mutate their cartObj argument's items IN PLACE.
      // Passing activeCart directly (the live store cart / exchangeCart, same object
      // reference) meant a discount that ultimately got rejected by the floor check
      // below still left the on-screen line item showing the discount -- the mutation
      // already happened before either setCart call in this function ever ran. Cloning
      // here isolates that mutation to a working copy; if the floor check rejects the
      // final commit, the live cart was never touched to begin with.
      let updatedCart = JSON.parse(JSON.stringify(activeCart));
      if (pricingRule.type === "FD" && Object.keys(selectedProductInCart).length > 0) {
        try {
          const newCart = await CheckoutFlatDiscount(selectedProductInCart, pricingRule, setCart, updatedCart, orderType, updatedCart, values.discountValue, matchingApproval);
          updatedCart = newCart;
        } catch (err) {
          console.error("Error applying flat discount:", err);
        }
      } else if (pricingRule.type === "PD" && Object.keys(selectedProductInCart).length > 0) {
        try {
          const newCart = await CheckoutPercentageDiscount(
            selectedProductInCart,
            pricingRule,
            setCart,
            updatedCart,
            orderType,
            updatedCart,
            values.discountValue,
            matchingApproval
          );
          updatedCart = newCart;
        } catch (err) {
          console.error("Error applying percentage discount:", err);
        }
      } else if (pricingRule.type === "TD") {
        try {
          const newCart = await CheckoutTotalManualDiscount(pricingRule, setCart, updatedCart, orderType, updatedCart, values.discountValue, matchingApproval);
          updatedCart = newCart;
        } catch (err) {
          console.error("Error applying total manual discount:", err);
        }
      }

      const itemsSummery = {
        decimalItems: { totalQty: 0, totalAmount: 0 },
        nonDecimalItems: { totalQty: 0, totalAmount: 0 },
        itemsQty: 0,
        items: 0,
      };

      const isInterState = getIsInterState(updatedCart);
      const groupedItemsByTaxRate = groupItemsByTaxRate(updatedCart.items, precision, { isInterState, filterCanceled: true });

      let updatedTotalTax = 0;
      let updatedTotalPrice = 0;
      let updatedTotalItemsQty = 0;
      let updatedTotalDiscounts = 0;
      updatedCart.items = updatedCart.items.map((item, i) => {
        if (item.isCanceled === "Y") {
          item.discount = 0;
          item.key = i;
          return item;
        }
        const precisionVal = tillData.tillAccess.csBunit.currencies[0].prcPrecision;
        const nettotalFixed = parseFloat(parseFloat(item.nettotal)?.toFixed(precisionVal));
        const taxAmountFixed = parseFloat(parseFloat(item.taxAmount)?.toFixed(precisionVal));
        const discountFixed = item.discount ? item.discount : 0;

        if (item?.isCanceled !== "Y") {
          updatedTotalPrice += nettotalFixed;
          updatedTotalItemsQty += item.weight;
          updatedTotalTax += taxAmountFixed;
          if (item.isCanceled !== "Y") {
            updatedTotalDiscounts += discountFixed;
          }
        }

        item.discount = discountFixed;
        item.key = i;

        if (!item.isGiftCard) {
          const itemTaxRate = item.taxRate ?? 0;
          const unitPrice = isFinite(item.nettotal / item.weight) ? (taxIncludeFlag === "Y" ? item.nettotal / item.weight / (1 + itemTaxRate / 100) : item.salePrice) : 0;

          const lineTax = item.listPrice * (1 + itemTaxRate / 100);
          const stdTax = item.sunitprice * (1 + itemTaxRate / 100);
          const grossUnit = Math.abs(item.sunitprice) - Math.abs(item.discount) / Math.abs(item.weight);
          const netList =
            taxIncludeFlag === "Y" ? item.listPrice - parseFloat(parseFloat(item.listPrice - item.listPrice / (1 + itemTaxRate / 100))?.toFixed(precisionVal)) : item.listPrice;
          const gross_list = taxIncludeFlag === "Y" ? item.listPrice : parseFloat(parseFloat(lineTax)?.toFixed(precisionVal));
          const grossStd = taxIncludeFlag === "Y" ? item.sunitprice : parseFloat(parseFloat(stdTax)?.toFixed(precisionVal));

          Object.assign(item, {
            linetax: nettotalFixed ? taxAmountFixed : 0,
            linenet: nettotalFixed ? parseFloat(parseFloat(item.nettotal - item.taxAmount)?.toFixed(precisionVal)) : 0,
            linegross: nettotalFixed || 0,
            linestd: nettotalFixed ? parseFloat(parseFloat(grossStd * item.weight)?.toFixed(precisionVal)) : 0,
            netunit: nettotalFixed ? parseFloat(parseFloat(unitPrice)?.toFixed(precisionVal)) : 0,
            listprice: nettotalFixed ? item.listPrice : 0,
            grossunit: nettotalFixed ? parseFloat(parseFloat(unitPrice)?.toFixed(precisionVal)) * (item.isReturn ? -1 : 1) : 0,
            grossstd: nettotalFixed ? grossStd : 0,
            grosslist: nettotalFixed ? roundTo(gross_list, precisionVal) : 0,
            netList: nettotalFixed ? netList : 0,
            unitPrice: nettotalFixed ? parseFloat(parseFloat(unitPrice)?.toFixed(precisionVal)) : 0,
            taxAmount: nettotalFixed ? taxAmountFixed : 0,
          });
        }

        if (Array.isArray(item.children) && item.children.length > 0) {
          item.children = item.children.map((child, ci) => {
            const childNettotalFixed = parseFloat(parseFloat(child.nettotal)?.toFixed(precisionVal));
            const childTaxAmountFixed = parseFloat(parseFloat(child.taxAmount)?.toFixed(precisionVal));
            const childDiscountFixed = child.discount ? child.discount : 0;
            child.discount = childDiscountFixed;
            child.key = ci;
            if (!child.isGiftCard) {
              const childUnitPrice = isFinite(child.nettotal / child.weight)
                ? taxIncludeFlag === "Y"
                  ? child.nettotal / child.weight / (1 + child.taxRate / 100)
                  : child.salePrice
                : 0;

              const childLineTax = child.listPrice * (1 + child.taxRate / 100);
              const childStdTax = child.sunitprice * (1 + child.taxRate / 100);
              const childNetList =
                taxIncludeFlag === "Y"
                  ? child.listPrice - parseFloat(parseFloat(child.listPrice - child.listPrice / (1 + child.taxRate / 100))?.toFixed(precisionVal))
                  : child.listPrice;
              const childGrossList = taxIncludeFlag === "Y" ? child.listPrice : parseFloat(parseFloat(childLineTax)?.toFixed(precisionVal));
              const childGrossStd = taxIncludeFlag === "Y" ? child.sunitprice : parseFloat(parseFloat(childStdTax)?.toFixed(precisionVal));

              Object.assign(child, {
                linetax: childNettotalFixed ? childTaxAmountFixed : 0,
                linenet: childNettotalFixed ? parseFloat(parseFloat(child.nettotal - child.taxAmount)?.toFixed(precisionVal)) : 0,
                linegross: childNettotalFixed || 0,
                linestd: childNettotalFixed ? parseFloat(parseFloat(childGrossStd * child.weight)?.toFixed(precisionVal)) : 0,
                netunit: childNettotalFixed ? parseFloat(parseFloat(childUnitPrice)?.toFixed(precisionVal)) : 0,
                listprice: childNettotalFixed ? child.listPrice : 0,
                grossunit: childNettotalFixed ? parseFloat(parseFloat(childUnitPrice)?.toFixed(precisionVal)) * (child.isReturn ? -1 : 1) : 0,
                grossstd: childNettotalFixed ? childGrossStd : 0,
                grosslist: childNettotalFixed ? childGrossList : 0,
                netList: childNettotalFixed ? childNetList : 0,
                unitPrice: childNettotalFixed ? parseFloat(parseFloat(childUnitPrice)?.toFixed(precisionVal)) : 0,
                taxAmount: childNettotalFixed ? childTaxAmountFixed : 0,
              });
            }
            return child;
          });
          item.children.forEach((child) => {
            const isDecimal = child.uomData?.[0]?.decimal === "Y" || child.isDecimal;
            const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";

            itemsSummery.itemsQty += child.weight;
            itemsSummery.items += 1;
            itemsSummery[summaryKey].totalQty += child.weight;
            itemsSummery[summaryKey].totalAmount += parseFloat(parseFloat(child.nettotal)?.toFixed(precisionVal));
          });
        } else {
          const isDecimal = item.uomData?.[0]?.decimal === "Y" || item.isDecimal;
          const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";

          itemsSummery.itemsQty += item.weight;
          itemsSummery.items += 1;
          itemsSummery[summaryKey].totalQty += item.weight;
          itemsSummery[summaryKey].totalAmount += parseFloat(parseFloat(item.nettotal)?.toFixed(precisionVal));
        }

        return item;
      });

      const sumLineTotals = (lines) => {
        let totalLineGross = 0;
        let totalLineTax = 0;

        lines.forEach((line) => {
          if (line.isCanceled === "Y") return;
          totalLineGross += line.nettotal;
          totalLineTax += line.taxAmount;
        });

        return { totalLineGross, totalLineTax };
      };
      const adjustOrderTotals = (order, lines) => {
        const { totalLineGross, totalLineTax } = sumLineTotals(lines);
        let adjustedOrderGross = order.total;
        let adjustedOrderTax = order.tax;

        const grossDifference = totalLineGross - adjustedOrderGross;
        const taxDifference = totalLineTax - adjustedOrderTax;

        // If grossDifference equals the existing roundOff, the gap is due to intentional
        // currency rounding — preserve the rounded total instead of correcting it back.
        // grossDifference = gross - rounded; roundOff = rounded - gross; sum = 0 when equal.
        const existingRoundOff = order.roundOff || 0;
        const isRoundingDifference = Math.abs(grossDifference + existingRoundOff) < 0.01;

        if (Math.abs(grossDifference) > 0.01 && !isRoundingDifference) {
          adjustedOrderGross += grossDifference;
        }

        if (Math.abs(taxDifference) > 0.01) {
          adjustedOrderTax += taxDifference;
        }

        return {
          ...order,
          total: adjustedOrderGross,
          tax: adjustedOrderTax,
        };
      };
      updatedCart = adjustOrderTotals(updatedCart, updatedCart.items);
      const freshTillData = useAuthStore.getState().tillData;
      const roundingMethod = getRoundingMethod(freshTillData);
      let finalTotal = updatedCart.total;
      let finalRoundOff = updatedCart.roundOff || 0;
      if (roundingMethod !== "NR") {
        const { roundedTotal: finalRoundedTotal, roundOffAmount: finalRoundOffAmount } = applyCurrencyRounding(updatedTotalPrice, roundingMethod, getPrecision(tillData));
        finalTotal = finalRoundedTotal;
        finalRoundOff = parseFloat(parseFloat(finalRoundOffAmount).toFixed(precision));
      }
      const allItemsInCart = (updatedCart.items || []).flatMap((item) => (Array.isArray(item.children) && item.children.length > 0 ? item.children : [item]));
      const updatedTotalStd = allItemsInCart.reduce((sum, item) => sum + (item.linestd || 0), 0);
      let finalCartObj = {
        ...updatedCart,
        items: [...updatedCart?.items],
        discount: updatedTotalDiscounts,
        totalQty: updatedTotalItemsQty,
        total: finalTotal,
        roundOff: finalRoundOff,
        subTotal: parseFloat(parseFloat(updatedTotalPrice - updatedTotalTax)?.toFixed(precision)),
        amountInWords: numberToWords(finalTotal),
        itemsSummery,
        groupedItemsByTaxRate,
        totalStd: updatedTotalStd,
      };
      let committed = true;
      if (isExchangeLocal) {
        localStorage.setItem("exchangeCartObj", JSON.stringify(finalCartObj));
        useCartStore.getState().setExchangeCart({ ...finalCartObj });
      } else {
        // BUG-2020 (2026-09-01): setCart now returns false when the floor check
        // rejects the write (a retrieved, paid Advance/Layaway order's total would
        // drop below what's already paid) -- must not claim success when it didn't.
        committed = setCart({ ...finalCartObj });
      }
      if (committed && updatedCart?.discountApplied !== false) {
        message.success("Discount applied successfully");
      }
      // BUG-2020 (2026-09-01): a rejected discount must not still sync to the server --
      // finalCartObj here reflects the discount that just got rejected by the floor
      // check above, not what the cart/table actually holds.
      if (committed && useRestaurantStore.getState().dineIn === "Y") {
        const selectedTableData = useRestaurantStore.getState().tableName;
        const tableRecord = await db.tableData.get(selectedTableData?.cwrFbTableId);
        let selectedTableRecord = tableRecord;
        const ordersFetched = await db.fbOrderData.where("cwrFbTableId").equals(selectedTableData?.cwrFbTableId).toArray();
        const activeOrder = ordersFetched.find((o) => o.fbOrderStatus === "IP");

        const enrichedItems = finalCartObj.items.map((line) => ({
          ...line,
          sendQty: line.order === "Y" ? 0 : 1,
          parkedItem: true,
          splitTable: selectedTableRecord?.splitTable || line.splitTable || null,
        }));

        updatedCart = {
          ...finalCartObj,
          tableNo: tableRecord?.name || selectedTableRecord?.name || selectedTableRecord?.tableName,
          splitTable: selectedTableRecord?.splitTable || null,
          items: enrichedItems,
        };

        if (activeOrder) {
          let updatedWsData = activeOrder.wsData;
          if (updatedWsData && typeof updatedWsData === "object" && Object.keys(updatedWsData).length > 0) {
            if (Array.isArray(updatedWsData.items) && updatedWsData.items.length > 0) {
              updatedWsData = {
                ...updatedWsData,
                items: updatedWsData.items.map((wsItem) => {
                  const matchingItem = finalCartObj.items.find((ci) => ci.fbOrderLineId === wsItem.fbOrderLineId);
                  if (matchingItem) {
                    return {
                      ...wsItem,
                      discount: matchingItem.discount,
                      nettotal: matchingItem.nettotal,
                      taxAmount: matchingItem.taxAmount,
                      salePrice: matchingItem.salePrice,
                      discountName: matchingItem.discountName || "",
                      mPricingruleId: matchingItem.mPricingruleId || "",
                    };
                  }
                  return wsItem;
                }),
                total: finalCartObj.total,
                tax: finalCartObj.tax,
                discount: finalCartObj.discount,
              };
            }
          } else {
            updatedWsData = {
              ...finalCartObj,
              items: enrichedItems,
            };
          }

          // [BUG-1597] fbOrderData.lines must stay an array of plain objects — that's
          // the shape KOT-fire writes (useOrderHandlers.js) and every other consumer
          // (void/cancel, copy-KOT, table transfer, SyncData's upsertFbOrder) expects.
          // Patch only the money/discount fields per matching fbOrderLineId so identity,
          // isCanceled, sOrderLineId and meta (relied on by cancel/void/reopen) survive
          // untouched. Rebuild from cart items if the existing lines were already
          // corrupted into GraphQL-fragment strings by a prior run of this same bug, or
          // don't exist yet.
          const hasValidLineObjects = Array.isArray(activeOrder.lines) && activeOrder.lines.length > 0 && typeof activeOrder.lines[0] === "object";
          const lineUpdatesByFbOrderLineId = new Map(
            (finalCartObj.items || []).filter((item) => item.fbOrderLineId).map((item) => [item.fbOrderLineId, item])
          );
          // [BUG-1597] linenet/linetax/linegross/netunit/grossunit/unitPrice on the cart
          // item are only refreshed by the discount-APPLY path's Object.assign block
          // (lines ~899-912) — removeAllDiscounts never recomputes them, so they'd be
          // stale (still showing discounted amounts) right after a discount is removed.
          // nettotal/taxAmount/salePrice/discount ARE refreshed by both apply and remove,
          // so derive the money fields from those instead of trusting the cached ones.
          // grossStd/lineStd/netList/netStd/unitTax are standard/list-price-based and
          // genuinely discount-invariant (see cart.js netStd/unitTax at add-time) — safe
          // to leave untouched when patching, or copy as-is when rebuilding.
          const correctedLines = hasValidLineObjects
            ? activeOrder.lines.map((line) => {
                const item = lineUpdatesByFbOrderLineId.get(line.fbOrderLineId);
                if (!item) return line;
                const nettotal = Number(item.nettotal) || 0;
                const taxAmount = Number(item.taxAmount) || 0;
                const unitPrice = Number(item.salePrice) || 0;
                return {
                  ...line,
                  discount: item.discount,
                  unitPrice,
                  netUnit: unitPrice,
                  lineNet: parseFloat((nettotal - taxAmount).toFixed(precision)),
                  lineTax: parseFloat(taxAmount.toFixed(precision)),
                  lineGross: parseFloat(nettotal.toFixed(precision)),
                  grossUnit: parseFloat((unitPrice * (item.isReturn ? -1 : 1)).toFixed(precision)),
                };
              })
            : (finalCartObj.items || []).map((item) => {
                const nettotal = Number(item.nettotal) || 0;
                const taxAmount = Number(item.taxAmount) || 0;
                const unitPrice = Number(item.salePrice) || 0;
                return {
                  fbOrderId: activeOrder.fbOrderId,
                  fbOrderLineId: item.fbOrderLineId,
                  mPoductId: item.productId,
                  mBatchId: item.mBatchId || null,
                  description: item.description || "",
                  csUomId: item.uom || item.csUomId || item.uomData?.[0]?.csUomId,
                  csTaxId: item.tax || item.cTaxId,
                  discount: item.discount,
                  line: 1,
                  qty: item.weight,
                  unitPrice,
                  netUnit: unitPrice,
                  listPrice: item.listprice ?? item.listPrice ?? 0,
                  lineNet: parseFloat((nettotal - taxAmount).toFixed(precision)),
                  lineTax: parseFloat(taxAmount.toFixed(precision)),
                  lineGross: parseFloat(nettotal.toFixed(precision)),
                  grossUnit: parseFloat((unitPrice * (item.isReturn ? -1 : 1)).toFixed(precision)),
                  sOrderLineId: null,
                  isOrdered: "Y",
                  isCanceled: item.isCanceled || "N",
                  grossStd: item.grossstd,
                  lineStd: item.linestd,
                  netList: item.netList,
                  netStd: item.netStd,
                  unitTax: item.unitTax,
                  meta: [],
                };
              });

          activeOrder.cart = updatedCart;
          activeOrder.lines = correctedLines;
          activeOrder.wsData = updatedWsData;
          activeOrder.fbOrderSync = "N";
          await db.fbOrderData.put(activeOrder, activeOrder.fbOrderId);

          await db.tableData.update(selectedTableData?.cwrFbTableId, {
            wsData: updatedWsData,
            tableSync: "N",
          });

          // The block above persists the discount to LOCAL IndexedDB. In online billing,
          // reopening a table rebuilds the cart from the SERVER's cartDetails snapshot AND
          // the server's own lines[]/discAmount, so the discount must also be pushed to the
          // server or discAmount stays stale even though item prices look right. Send the
          // just-corrected lines (with skipKots so no KOTs are re-fired) instead of a stale
          // pre-discount snapshot.
          try {
            await SyncData(
              {
                fbOrderId: activeOrder.fbOrderId,
                cwrFbTableId: selectedTableData?.cwrFbTableId,
                cwrFbsectionId: activeOrder.cwrFbsectionId,
                fbOrderStatus: "IP",
                isCanceled: "N",
                lines: correctedLines,
                skipKots: true,
                splitTable: activeOrder.splitTable || null,
              },
              "upsertFbOrder",
              null,
              "",
              true,
              updatedCart
            );
          } catch (e) {
            console.error("[BUG-1597] discount server sync failed:", e);
          }
        }
      }
  };

  // BUG-2020 (proactive floor check, manual discount path): same rule as the auto/coupon
  // Total Bill Discount check in useCartHandlers.js's processBillDiscounts -- a manual
  // discount that drops a retrieved Advance/Layaway order's total below the amount already
  // paid must be rejected on the spot, not just caught later at final save. These two
  // wrapper functions call the discount function and do nothing else afterward (no further
  // outer setCart re-commit), so checking the live store cart immediately after is safe here.
  const checkFloorAfterManualDiscount = (preDiscountCartSnapshot) => {
    if (isExchange) return;
    const liveCartAfterDiscount = useCartStore.getState().cart || {};
    const floorViolation = advanceOrderFloorViolation(liveCartAfterDiscount, liveCartAfterDiscount.total);
    if (floorViolation) {
      useCartStore.getState().setCart(preDiscountCartSnapshot);
      Modal.warning({
        title: <div style={{ fontWeight: "500", fontSize: "1.2em", color: "#0F0718" }}>Amount Cannot Be Reduced Below Paid Amount</div>,
        content: (
          <div style={{ fontSize: "14px", color: "#0F0718", marginTop: "8px" }}>
            <p style={{ marginBottom: 0, color: "#0F0718", fontSize: "1em" }}>{floorViolation}</p>
          </div>
        ),
        icon: <WarningOutlined />,
        okText: "Ok",
        okButtonProps: { style: { display: "inline-block" } },
        modalRender: (node) => <div className="custom-confirm-modal-wrapper">{node}</div>,
      });
    }
  };

  const processLineManualDiscount = () => {
    const pricingRule = manualDiscountTypes[manualDiscountTypes.findIndex((md) => md.mPricingrulesId === selectedManualDiscountType)];
    const preDiscountCartSnapshot = useCartStore.getState().cart;
    if (pricingRule.type === "V") {
      CheckoutFlatDiscount(cart, setCart, selectedRowKeys, pricingRule, manualDiscountInput, tillaccess);
    }

    if (pricingRule.type === "P") {
      CheckoutPercentageDiscount(cart, setCart, selectedRowKeys, pricingRule, manualDiscountInput, tillaccess);
    }
    checkFloorAfterManualDiscount(preDiscountCartSnapshot);
    setSelectedRowKeys([]);
  };

  const processTotalManualDiscount = (discountParam) => {
    const preDiscountCartSnapshot = useCartStore.getState().cart;
    CheckoutTotalManualDiscount(discountParam, setCart, cart, manualDiscountTypes, selectedManualDiscountType, manualDiscountInput, tillaccess);
    checkFloorAfterManualDiscount(preDiscountCartSnapshot);
  };

  const removeAllDiscounts = async (discountFlag = false, type = false, awaitPersist = false) => {
    setManualDiscountModalVisible(false);
    setManualDiscountInput(null);
    setSelectedManualDiscountTypeValueState(null);
    const cartItems = [...(isExchange ? useCartStore.getState().exchangeCart?.items || [] : cart.items || [])];
    let updatedCart = { ...(isExchange ? useCartStore.getState().exchangeCart : cart), items: cartItems };
    function calculateCartItem(addedToCart) {
      if (addedToCart.isCanceled === "Y") {
        addedToCart.discount = 0;
        return addedToCart;
      }
      if (addedToCart.children && addedToCart.children.length > 0) {
        addedToCart.children = addedToCart.children.map((child) => calculateCartItem(child));
        addedToCart.children.forEach((child, index) => {
          const taxInclude = (child.realPrice * child.taxRate) / 100;
          const sp = ((taxIncludeFlag === "Y" ? child.realPrice : child.realPrice + taxInclude) * child.weight || child.nettotal) - child.discount;
          const tax = sp - sp / (1 + child.taxRate / 100);
          child.nettotal = sp;
          child.taxAmount = tax;
        });
        const childrenNetTotal = addedToCart.children.reduce((sum, child) => sum + (child.nettotal || 0), 0);
        const childrenTaxAmount = addedToCart.children.reduce((sum, child) => sum + (child.taxAmount || 0), 0);

        addedToCart.salePrice = addedToCart.realPrice;
        addedToCart.taxAmount = parseFloat(parseFloat(childrenTaxAmount)?.toFixed(precision));
        addedToCart.nettotal = parseFloat(parseFloat(childrenNetTotal)?.toFixed(precision));
        addedToCart.discount = 0;
        addedToCart.discountName = "";
        addedToCart.nextRule = "N";
        addedToCart.mPricingruleId = "";
        // Live-reported (GPR): Total Bill Discount silently vanished after customer
        // attach/remove. Root cause -- this reset zeroed addedToCart.discount but left
        // allDiscounts[].appliedAmount (a per-rule record of what THIS item's discount used
        // to be) stale. The very next line below replays every persisted discount via
        // CheckoutTotalManualDiscount, whose getOwnPriorContribution() trusts that stale
        // appliedAmount as "already baked into item.discount" (true for every other caller,
        // which never resets one without the other) and subtracts it back out of the freshly
        // computed share -- netting a real discount down to ~0. Reset allDiscounts/approval
        // here too so a reapply starts genuinely fresh, matching this function's own reset of
        // every other discount-derived field above.
        addedToCart.allDiscounts = [];
        addedToCart.approval = [];
        delete addedToCart.priority;
      } else {
        const sp = parseFloat(addedToCart.realPrice);
        const mrp = parseFloat(parseFloat(parseFloat(sp) * addedToCart.weight)?.toFixed(precision));
        const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + addedToCart.taxRate / 100) : mrp * (addedToCart.taxRate / 100);
        addedToCart.salePrice = sp;
        addedToCart.taxAmount = parseFloat(parseFloat(tax)?.toFixed(precision));
        addedToCart.mPricingruleId = "";
        addedToCart.nettotal = taxIncludeFlag === "Y" ? mrp : parseFloat(parseFloat(parseFloat(mrp) + tax)?.toFixed(precision));
        addedToCart.discount = 0;
        addedToCart.discountName = "";
        addedToCart.nextRule = "N";
        // See comment above (children branch) -- same stale-appliedAmount fix.
        addedToCart.allDiscounts = [];
        addedToCart.approval = [];
        delete addedToCart.priority;
      }
      // linegross (and the other derived fields enrichLineItemFields produces) was never
      // refreshed here, even though nettotal/taxAmount/discount are reset above -- it stayed
      // pinned to the discounted nettotal from the last time a discount was applied. The
      // reactive TCS recompute in useAdditionalCharges.js reads item.linegross (via
      // defaultTcsItemValue) to determine each line's TCS-eligible value, so removing a
      // discount left TCS computed off the stale discounted amount forever after, even
      // though the cart's own nettotal/total were correctly restored to full price. Mirrors
      // the same enrichLineItemFields call CheckoutTotalManualDiscount.js's reset branch
      // already makes for the identical reason.
      enrichLineItemFields(addedToCart, taxIncludeFlag, precision);
      return addedToCart;
    }

    const pricingRuleIndex = manualDiscountTypes.findIndex((md) => md.mPricingrulesId === selectedProductInCart?.mPricingruleId);

    cartItems.forEach((addedToCart, i) => {
      if ((pricingRuleIndex === undefined || !manualDiscountTypes[pricingRuleIndex]) && !type) {
        updatedCart.manualDiscountData = {
          totalLevelDiscount: [],
          lineLevelDiscount: [],
        };
        addedToCart.allDiscounts = [];
        addedToCart.approval = [];
        cartItems[i] = calculateCartItem(addedToCart);
        return;
      }
      if (discountFlag && manualDiscountTypes[pricingRuleIndex].type !== "TD" && selectedProductInCart.lineId === addedToCart.lineId) {
        updatedCart.manualDiscountData.lineLevelDiscount = updatedCart.manualDiscountData?.lineLevelDiscount?.filter((item) => item.lineId !== addedToCart.lineId);
        addedToCart.allDiscounts = (addedToCart.allDiscounts || [])?.filter((item) => item.mPricingruleId !== manualDiscountTypes[pricingRuleIndex].mPricingrulesId);
        addedToCart.approval = (addedToCart?.approval || [])?.filter((item) => item.mPricingrulesId !== manualDiscountTypes[pricingRuleIndex].mPricingrulesId);
      } else if (
        discountFlag &&
        manualDiscountTypes[pricingRuleIndex].type === "TD" &&
        selectedProductInCart.mPricingruleId === manualDiscountTypes[pricingRuleIndex].mPricingrulesId
      ) {
        updatedCart.manualDiscountData.totalLevelDiscount = updatedCart.manualDiscountData?.totalLevelDiscount?.filter(
          (item) => item.pricingRule !== manualDiscountTypes[pricingRuleIndex].mPricingrulesId
        );
        addedToCart.allDiscounts = (addedToCart.allDiscounts || [])?.filter((item) => item.mPricingruleId !== manualDiscountTypes[pricingRuleIndex].mPricingrulesId);
        addedToCart.approval = (addedToCart?.approval || [])?.filter((item) => item.mPricingrulesId !== manualDiscountTypes[pricingRuleIndex].mPricingrulesId);
      } else if (!discountFlag && Object.keys(selectedProductInCart).length === 0 && !type) {
        updatedCart.manualDiscountData = {};
      }
      cartItems[i] = calculateCartItem(addedToCart);
    });

    delete cart["manualDiscountApplied"];

    const dineInSyncNeeded = tillaccess?.layout === "2" && useRestaurantStore.getState().dineIn === "Y" && useRestaurantStore.getState().tableName;

    let updatedTotalTax = 0;
    let updatedTotalPrice = 0;
    let updatedTotalItemsQty = 0;
    let updatedTotalDiscounts = 0;

    if (updatedCart?.manualDiscountData?.lineLevelDiscount?.length > 0) {
      for (const item of updatedCart.manualDiscountData.lineLevelDiscount) {
        // Fall back to the always-loaded pricing-rules cache: manualDiscountTypes state is only
        // populated when the Manual Discount modal is opened, so on a fresh load (or restored cart)
        // it can be empty — which would silently drop the manual line discount on re-apply.
        const pricingRule =
          manualDiscountTypes.find((md) => md.mPricingrulesId === item.pricingRule) ||
          getPricingRules().find((r) => r.mPricingrulesId === item.pricingRule);
        let productSelected = updatedCart.items.filter((cartItem) => cartItem.productId === item.productId);
        if (pricingRule?.type === "FD") {
          try {
            const newCart = await CheckoutFlatDiscount(productSelected[0], pricingRule, setCart, updatedCart, orderType, updatedCart, item.discountValue);
            updatedCart = newCart;
          } catch (err) {
            console.error("Error applying flat discount:", err);
          }
        } else if (pricingRule?.type === "PD") {
          try {
            const newCart = await CheckoutPercentageDiscount(productSelected[0], pricingRule, setCart, updatedCart, orderType, updatedCart, item.discountValue);
            updatedCart = newCart;
          } catch (err) {
            console.error("Error applying percentage discount:", err);
          }
        }
      }
    }

    if (updatedCart?.manualDiscountData?.totalLevelDiscount?.length > 0) {
      for (const item of updatedCart.manualDiscountData.totalLevelDiscount) {
        // Fall back to the always-loaded pricing-rules cache: manualDiscountTypes state is only
        // populated when the Manual Discount modal is opened, so on a fresh load (or restored cart)
        // it can be empty — which would silently drop the manual line discount on re-apply.
        const pricingRule =
          manualDiscountTypes.find((md) => md.mPricingrulesId === item.pricingRule) ||
          getPricingRules().find((r) => r.mPricingrulesId === item.pricingRule);
        if (pricingRule?.type === "TD") {
          try {
            const newCart = await CheckoutTotalManualDiscount(pricingRule, setCart, updatedCart, orderType, updatedCart, item.discountValue);
            updatedCart = newCart;
          } catch (err) {
            console.error("Error applying Total discount:", err);
          }
        }
      }
    }

    const itemSummery = {
      decimalItems: { totalQty: 0, totalAmount: 0 },
      nonDecimalItems: { totalQty: 0, totalAmount: 0 },
      itemsQty: 0,
      items: 0,
    };

    const isInterState2 = getIsInterState(updatedCart);
    const groupedItemsByTaxRate = groupItemsByTaxRate(updatedCart.items, precision, { isInterState: isInterState2, filterCanceled: true });

    updatedCart.items = updatedCart.items.map((item, i) => {
      if (item.isCanceled === "Y") {
        item.discount = 0;
        item.key = i;
        return item;
      }
      const nettotalFixed = parseFloat(parseFloat(item.nettotal)?.toFixed(precision));
      const taxAmountFixed = parseFloat(parseFloat(item.taxAmount)?.toFixed(precision));
      const discountFixed = item.discount ? parseFloat(parseFloat(item.discount)?.toFixed(precision)) : 0;

      updatedTotalPrice = parseFloat(parseFloat(updatedTotalPrice + nettotalFixed)?.toFixed(precision));
      updatedTotalItemsQty = parseFloat(parseFloat(updatedTotalItemsQty + item.weight)?.toFixed(precision));
      updatedTotalTax = parseFloat(parseFloat(updatedTotalTax + taxAmountFixed)?.toFixed(precision));
      updatedTotalDiscounts = parseFloat(parseFloat(updatedTotalDiscounts + discountFixed)?.toFixed(precision));

      item.discount = discountFixed;
      item.key = i;
      item.nettotal = parseFloat(parseFloat(item.nettotal)?.toFixed(precision));

      if (Array.isArray(item.children) && item.children.length > 0) {
        item.children.forEach((child) => {
          const isDecimal = child.uomData?.[0]?.decimal === "Y" || child.isDecimal;
          const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";

          itemSummery.itemsQty += child.weight;
          itemSummery.items += 1;
          itemSummery[summaryKey].totalQty += child.weight;
          itemSummery[summaryKey].totalAmount += parseFloat(parseFloat(child.nettotal)?.toFixed(precision));
        });
      } else {
        const isDecimal = item.uomData?.[0]?.decimal === "Y" || item.isDecimal;
        const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";

        itemSummery.itemsQty += item.weight;
        itemSummery.items += 1;
        itemSummery[summaryKey].totalQty += item.weight;
        itemSummery[summaryKey].totalAmount += parseFloat(parseFloat(item.nettotal)?.toFixed(precision));
      }

      return item;
    });

    const sumLineTotals = (lines) => {
      let totalLineGross = 0;
      let totalLineTax = 0;

      lines.forEach((line) => {
        if (line.isCanceled === "Y") return;
        totalLineGross += line.nettotal;
        totalLineTax += line.taxAmount;
      });

      return { totalLineGross, totalLineTax };
    };
    const adjustOrderTotals = (order, lines) => {
      const { totalLineGross, totalLineTax } = sumLineTotals(lines);
      let adjustedOrderGross = order.total;
      let adjustedOrderTax = order.tax;

      const grossDifference = totalLineGross - adjustedOrderGross;
      const taxDifference = totalLineTax - adjustedOrderTax;

      // If grossDifference equals the existing roundOff, the gap is due to intentional
      // currency rounding — preserve the rounded total instead of correcting it back.
      // grossDifference = gross - rounded; roundOff = rounded - gross; sum = 0 when equal.
      const existingRoundOff = order.roundOff || 0;
      const isRoundingDifference = Math.abs(grossDifference + existingRoundOff) < 0.01;

      if (Math.abs(grossDifference) > 0.01 && !isRoundingDifference) {
        adjustedOrderGross += grossDifference;
      }

      if (Math.abs(taxDifference) > 0.01) {
        adjustedOrderTax += taxDifference;
      }

      return {
        ...order,
        total: adjustedOrderGross,
        tax: adjustedOrderTax,
      };
    };
    updatedCart = adjustOrderTotals(updatedCart, updatedCart.items);
    const freshTillData = useAuthStore.getState().tillData;
    const roundingMethod = getRoundingMethod(freshTillData);
    let finalTotal = updatedCart.total;
    let finalRoundOff = updatedCart.roundOff || 0;
    if (roundingMethod !== "NR") {
      const { roundedTotal: finalRoundedTotal, roundOffAmount: finalRoundOffAmount } = applyCurrencyRounding(updatedTotalPrice, roundingMethod, getPrecision(tillData));
      finalTotal = finalRoundedTotal;
      finalRoundOff = parseFloat(parseFloat(finalRoundOffAmount).toFixed(precision));
    }
    const allItemsInCart = (updatedCart.items || []).flatMap((item) => (Array.isArray(item.children) && item.children.length > 0 ? item.children : [item]));
    const updatedTotalStd = allItemsInCart.reduce((sum, item) => sum + (item.linestd || 0), 0);
    let finalCartObj = {
      ...updatedCart,
      items: [...updatedCart.items],
      discount: updatedTotalDiscounts,
      totalQty: updatedTotalItemsQty,
      total: finalTotal,
      roundOff: finalRoundOff,
      tax: roundTo(updatedTotalTax, getPrecision(tillData)),
      subTotal: parseFloat(parseFloat(updatedTotalPrice - updatedTotalTax)?.toFixed(precision)),
      totalDiscountFlag: !discountFlag || Object.keys(selectedProductInCart).length === 0 ? false : true,
      groupedItemsByTaxRate,
      totalStd: updatedTotalStd,
      // BUG-2189: the discount-APPLY path recomputes itemsSummery/amountInWords and
      // stamps discountApplied=true, but this removal path rebuilt the money fields
      // and left all three carrying the previously-discounted figures -- a cart
      // object internally contradicting its own restored total. itemSummery is
      // already computed from the restored nettotals in the loop above; reuse it.
      // (amountInWords is pre-TCS here; the TCS effect in useAdditionalCharges.js
      // refreshes it to the TCS-inclusive total for TCS orders, and this value is
      // already correct for non-TCS carts.)
      itemsSummery: itemSummery,
      amountInWords: numberToWords(finalTotal),
      discountApplied: (updatedCart?.manualDiscountData?.totalLevelDiscount?.length > 0 || updatedCart?.manualDiscountData?.lineLevelDiscount?.length > 0),
    };
    if (discountFlag) {
      if (isExchange) {
        localStorage.setItem("exchangeCartObj", JSON.stringify(finalCartObj));
        useCartStore.getState().setExchangeCart({ ...finalCartObj });
      } else {
        setCart(finalCartObj);
      }
    }

    if (dineInSyncNeeded) {
      const tableInfo = useRestaurantStore.getState().tableName;
      const persistDiscountRemoval = async () => {
        const ordersFetched = await db.fbOrderData.where("cwrFbTableId").equals(tableInfo?.cwrFbTableId).toArray();
        for (const fbOrder of ordersFetched) {
          if (fbOrder.fbOrderStatus === "IP") {
                fbOrder.cart = { ...finalCartObj };
                fbOrder.fbOrderSync = "N";

                // [BUG-1597] Mirror the apply path: keep fbOrderData.lines as plain
                // objects (the shape every other consumer — void/cancel, copy-KOT,
                // SyncData — expects) and patch only money/discount fields by
                // fbOrderLineId, preserving identity, isCanceled, sOrderLineId and meta.
                // Rebuild from cart items if the existing lines were already corrupted
                // into strings or don't exist yet.
                const hasValidLineObjects = Array.isArray(fbOrder.lines) && fbOrder.lines.length > 0 && typeof fbOrder.lines[0] === "object";
                const lineUpdatesByFbOrderLineId = new Map(
                  (finalCartObj.items || []).filter((item) => item.fbOrderLineId).map((item) => [item.fbOrderLineId, item])
                );
                // [BUG-1597] linenet/linetax/linegross/netunit/grossunit/unitPrice on the
                // cart item are only refreshed by the discount-APPLY path — removeAllDiscounts
                // (this path) never recomputes them, so they'd be stale (still discounted)
                // right after removal. nettotal/taxAmount/salePrice/discount ARE refreshed
                // here (see calculateCartItem above), so derive the money fields from those.
                // grossStd/lineStd/netList/netStd/unitTax are standard/list-price-based and
                // discount-invariant — safe to leave untouched when patching, or copy as-is
                // when rebuilding.
                const correctedLines = hasValidLineObjects
                  ? fbOrder.lines.map((line) => {
                      const item = lineUpdatesByFbOrderLineId.get(line.fbOrderLineId);
                      if (!item) return line;
                      const nettotal = Number(item.nettotal) || 0;
                      const taxAmount = Number(item.taxAmount) || 0;
                      const unitPrice = Number(item.salePrice) || 0;
                      return {
                        ...line,
                        discount: item.discount,
                        unitPrice,
                        netUnit: unitPrice,
                        lineNet: parseFloat((nettotal - taxAmount).toFixed(precision)),
                        lineTax: parseFloat(taxAmount.toFixed(precision)),
                        lineGross: parseFloat(nettotal.toFixed(precision)),
                        grossUnit: parseFloat((unitPrice * (item.isReturn ? -1 : 1)).toFixed(precision)),
                      };
                    })
                  : (finalCartObj.items || []).map((item) => {
                      const nettotal = Number(item.nettotal) || 0;
                      const taxAmount = Number(item.taxAmount) || 0;
                      const unitPrice = Number(item.salePrice) || 0;
                      return {
                        fbOrderId: fbOrder.fbOrderId,
                        fbOrderLineId: item.fbOrderLineId,
                        mPoductId: item.productId,
                        mBatchId: item.mBatchId || null,
                        description: item.description || "",
                        csUomId: item.uom || item.csUomId || item.uomData?.[0]?.csUomId,
                        csTaxId: item.tax || item.cTaxId,
                        discount: item.discount,
                        line: 1,
                        qty: item.weight,
                        unitPrice,
                        netUnit: unitPrice,
                        listPrice: item.listprice ?? item.listPrice ?? 0,
                        lineNet: parseFloat((nettotal - taxAmount).toFixed(precision)),
                        lineTax: parseFloat(taxAmount.toFixed(precision)),
                        lineGross: parseFloat(nettotal.toFixed(precision)),
                        grossUnit: parseFloat((unitPrice * (item.isReturn ? -1 : 1)).toFixed(precision)),
                        sOrderLineId: null,
                        isOrdered: "Y",
                        isCanceled: item.isCanceled || "N",
                        grossStd: item.grossstd,
                        lineStd: item.linestd,
                        netList: item.netList,
                        netStd: item.netStd,
                        unitTax: item.unitTax,
                        meta: [],
                      };
                    });
                fbOrder.lines = correctedLines;

                if (fbOrder.wsData && typeof fbOrder.wsData === "object" && Object.keys(fbOrder.wsData).length > 0) {
                  if (Array.isArray(fbOrder.wsData.items) && fbOrder.wsData.items.length > 0) {
                    fbOrder.wsData = {
                      ...fbOrder.wsData,
                      items: fbOrder.wsData.items.map((wsItem) => {
                        const matchingCartItem = finalCartObj.items.find((ci) => ci.fbOrderLineId === wsItem.fbOrderLineId);
                        if (matchingCartItem) {
                          return {
                            ...wsItem,
                            discount: matchingCartItem.discount,
                            nettotal: matchingCartItem.nettotal,
                            taxAmount: matchingCartItem.taxAmount,
                            salePrice: matchingCartItem.salePrice,
                            discountName: matchingCartItem.discountName || "",
                            mPricingruleId: matchingCartItem.mPricingruleId || "",
                          };
                        }
                        return wsItem;
                      }),
                      total: finalCartObj.total,
                      tax: finalCartObj.tax,
                      discount: finalCartObj.discount,
                    };
                  }
                } else {
                  fbOrder.wsData = { ...finalCartObj };
                }

                await db.fbOrderData.put(fbOrder, fbOrder.fbOrderId);

                await db.tableData.update(tableInfo?.cwrFbTableId, {
                  wsData: fbOrder.wsData,
                  tableSync: "N",
                });

                // Mirror the apply path: push the removal to the server too, otherwise
                // online reopen would restore the discount from the stale server snapshot.
                try {
                  await SyncData(
                    {
                      fbOrderId: fbOrder.fbOrderId,
                      cwrFbTableId: tableInfo?.cwrFbTableId,
                      cwrFbsectionId: fbOrder.cwrFbsectionId,
                      fbOrderStatus: "IP",
                      isCanceled: "N",
                      lines: correctedLines,
                      skipKots: true,
                      splitTable: fbOrder.splitTable || null,
                    },
                    "upsertFbOrder",
                    null,
                    "",
                    true,
                    finalCartObj
                  );
                } catch (e) {
                  console.error("[BUG-1597] discount-removal server sync failed:", e);
                }
              }
        }
      };

      // [BUG-1795] The "Remove All Discounts" button needs this persisted before the
      // caller can safely consider the removal durable (a refresh right after would
      // otherwise reopen the table from a stale pre-removal fbOrderData record). Every
      // other caller (coupon removal, customer change) relies on the original
      // fire-and-forget timing and must not be delayed by a network round trip it
      // never awaited before — so only await here when explicitly requested.
      if (awaitPersist) {
        try {
          await persistDiscountRemoval();
        } catch (err) {
          console.error("[BUG-1795] discount-removal DB/sync error:", err);
        }
      } else {
        persistDiscountRemoval().catch((err) => {
          console.error("[BUG-1597] discount-removal DB/sync error:", err);
        });
      }
    }

    if (searchInputRef?.current) {
      searchInputRef?.current?.focus();
    }
    return finalCartObj;
  };

  return {
    // State
    isQtyUpdate,
    setIsQtyUpdate,
    showPaymentMethods,
    setShowPaymentMethods,
    selectedProductInCart,
    setSelectedProductInCart,
    selectedProductQty,
    setSelectProductQty,
    selectedProduct,
    setSelectedProduct: setSelectedProductLocal,
    selectedRowKeys,
    setSelectedRowKeys,
    selectedKeys,
    setSelectedKeys,
    isInputFocused,
    setIsInputFocused,
    inputFocused,
    setInputFocused,
    realPrice,
    setRealPrice,
    firstInputValue,
    setFirstInputValue,
    qtyNumberFlag,
    setQtyNumberFlag,

    // Manual discount state
    manualDiscountModalVisible,
    setManualDiscountModalVisible,
    manualDiscountInput,
    setManualDiscountInput,
    manualDiscountTypes,
    setManualDiscountTypes,
    selectedManualDiscountType,
    setSelectedManualDiscountTypeValue,
    enableManualDiscountInput,
    setEnableManualDiscountInput,
    manualDiscountForm,
    totalBillDiscountData,
    setTotalBillDiscountData,
    discountAdditionalInfo,
    setDiscountAdditionalInfo,
    discountVerificationData,
    setDiscountVerifictionData,
    discountAdditionalInfoForm,
    selectedManualDiscountTypeValueState,
    setSelectedManualDiscountTypeValueState,

    // Functions
    selectProductInCart,
    clearSelectedProductInCart,
    enterTotalQty,
    enterRealPrice,
    onChangeTotalQuantity,
    handleTotalQty,
    handleRealPrice,
    deleteProduct,
    decreaseProductQty,
    increaseProductQty,
    deleteReceipt,
    deleteCart,
    applyManualDiscount,
    processLineManualDiscount,
    processTotalManualDiscount,
    removeAllDiscounts,
    processManualDiscount,
  };
};

export default useCartOperations;
